<template>
    <b-modal
        :id="id"
        :title="title"
        centered
        no-close-on-backdrop
        @hidden="close"
    >
        <div v-if="isRefundCreditPossible">
            <div class="form-group col-12">
                <h6>
                    Paid amount : {{ paymentDetails.price | formatPrice }}
                </h6>
            </div>

            <div class="form-group col-12">
                <label for="amount">Refund amount</label>

                <currency-input
                    id="amount"
                    v-model="amount"
                    :disabled="isLoading"
                    class="form-control"
                    :class="{
                        'is-invalid': hasError('amount')
                    }"
                />

                <div class="invalid-feedback">
                    {{ getError('amount') }}
                </div>
            </div>
        </div>

        <div v-if="isRefundStarsPossible">
            <div class="form-group col-12">
                <h6> Stars used : {{ starsUsed }} </h6>
            </div>

            <div class="form-group col-12">
                <label for="stars">Refund stars</label>

                <input
                    id="stars"
                    v-model.number="stars"
                    type="number"
                    :min="0"
                    :step="1"
                    :disabled="isLoading"
                    class="form-control"
                    :class="{
                        'is-invalid': hasError('stars')
                    }"
                />

                <div class="invalid-feedback">
                    {{ getError('stars') }}
                </div>
            </div>
        </div>

        <template #modal-footer>
            <div class="col-12 d-flex justify-content-between">
                <button
                    :disabled="isLoading"
                    type="button"
                    class="btn btn-danger waves-effect waves-light mr-2"
                    @click="close"
                >
                    Cancel
                </button>

                <button
                    :disabled="isLoading"
                    type="submit"
                    class="btn btn-success waves-effect waves-light"
                    @click="refund"
                >
                    Refund
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { StatusCodes as HTTP } from 'http-status-codes';
import { mapGetters, mapActions } from 'vuex';
import { requiredIf } from 'vuelidate/lib/validators';
import CurrencyInput from '@/components/elements/CurrencyInput';

export default {
    components: {
        CurrencyInput
    },

    props: {
        id: {
            type: String,
            required: false,
            default: 'course-refund'
        },
        userId: {
            type: String,
            required: false,
            default: ''
        },
        courseId: {
            type: String,
            required: true
        },
        paymentDetails: {
            type: Object,
            required: true
        },
        starsUsed: {
            type: Number,
            required: true
        },
        paymentMethod: {
            type: String,
            required: false,
            default: ''
        }
    },

    data() {
        return {
            amount: 0,
            stars: 0,
            serverErrors: [],
            title: 'Refund',
            isLoading: false
        };
    },

    computed: {
        ...mapGetters({
            PAYMENT_METHOD_STARS: 'courses/PAYMENT_METHOD_STARS',
            PAYMENT_METHOD_CREDIT: 'courses/PAYMENT_METHOD_CREDIT',
            PAYMENT_METHOD_CREDIT_AND_STARS:
                'courses/PAYMENT_METHOD_CREDIT_AND_STARS'
        }),

        isRefundCreditPossible() {
            return [
                this.PAYMENT_METHOD_CREDIT,
                this.PAYMENT_METHOD_CREDIT_AND_STARS
            ].includes(this.paymentMethod);
        },

        isRefundStarsPossible() {
            return [
                this.PAYMENT_METHOD_STARS,
                this.PAYMENT_METHOD_CREDIT_AND_STARS
            ].includes(this.paymentMethod);
        }
    },

    validations: {
        amount: {
            required: requiredIf(function () {
                return this.isRefundCreditPossible;
            }),
            amountTooHigh(v) {
                return this.isRefundCreditPossible
                    ? v <= this.paymentDetails.price
                    : true;
            }
        },
        stars: {
            required: requiredIf(function () {
                return this.isRefundStarsPossible;
            }),
            starsTooMany(v) {
                return this.isRefundStarsPossible ? v <= this.starsUsed : true;
            }
        }
    },

    methods: {
        ...mapActions({
            refundFunds: 'courses/refund'
        }),

        async refund() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                this.isLoading = true;

                const data = {
                    id: this.courseId,
                    userId: this.userId
                };

                if (this.isRefundCreditPossible) {
                    data.amount = this.amount;
                }

                if (this.isRefundStarsPossible) {
                    data.stars = this.stars;
                }

                await this.refundFunds(data);

                this.$toaster('The funds has been refunded!');

                this.$emit('success', {
                    userId: this.userId,
                    courseId: this.courseId,
                    amount: this.amount
                });

                this.isLoading = false;

                this.close();
                this.resetForm();
            } catch (error) {
                console.error(error);

                const { response } = error;
                let errorMessage = 'The funds could not be refunded';

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        errorMessage = data;

                        this.$toasterError(errorMessage);

                        return;
                    }

                    if (status === HTTP.BAD_REQUEST && data?.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');
                    }
                }
            } finally {
                this.isLoading = false;
            }
        },

        close() {
            this.$bvModal.hide(this.id);
        },

        hasError(key) {
            return (
                this.$v[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v,
                this.serverErrors,
                key
            );
        },

        resetForm() {
            this.stars = 0;
            this.amount = 0;

            this.$v.$reset();
        }
    }
};
</script>
